import { Esqueleto, Faixa, Icone, ListaRotinas, type ItemRotina } from '@persona/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowUpRight, Clock, ListChecks, Star } from 'lucide-react';
import { lazy, Suspense, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { api } from '../src/api/cliente';
import { mensagemDe } from '../src/api/erros';
import { usePreCarregar } from '../src/navegacao/preCarregar';
import { useSessao } from '../src/sessao/sessao';
import { NumerosDia, PendenciasDia, useResumoDia } from './ResumoDia';
import { usePaletaComLetra } from './Shell';

// Mural em pedaço próprio: imagens e textos de propaganda não pesam no JS inicial (orçamento 07 §6.1).
const Mural = lazy(() => import('./Mural').then((m) => ({ default: m.Mural })));

export interface ResumoInicio {
  modulos: { codigo: string; nome: string; icone?: string | null; contador?: string | null }[];
  favoritos: { codigo: string; nome: string; modulo: string; icone?: string | null }[];
  recentes: { codigo: string; nome: string; modulo: string; icone?: string | null }[];
  avisos: { tipo: string; severidade: 'aviso' | 'erro'; mensagem: string; rotina?: string }[];
  notificacoesNaoLidas: number;
}

/** "Quarta, 28 de outubro": a data-base por extenso, sem o ano. */
function dataPorExtenso(dataBase: string | null) {
  // Só a parte AAAA-MM-DD, ao meio-dia local: aceita data com hora ou fuso sem virar data inválida nem trocar o dia.
  const dia = /^\d{4}-\d{2}-\d{2}/.exec(dataBase ?? '')?.[0];
  const d = dia ? new Date(`${dia}T12:00:00`) : new Date();
  const texto = new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }).format(d).replace('-feira', '');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function saudacao(hora: number) {
  return hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';
}

/** Tela inicial — Persona Experience (07 §2.3): saudação, números e pendências do dia (painéis dos módulos), mural da Personalitec, grade de módulos navegável por setas, recentes e favoritos. */
export function TelaInicial() {
  const { sessao, empresa, filial, dataBase } = useSessao();
  const resumo = useQuery({ queryKey: ['inicio'], queryFn: () => api.get<ResumoInicio>('/inicio/resumo') });
  const grade = useRef<HTMLDivElement>(null);
  usePaletaComLetra();

  const moverFoco = (e: KeyboardEvent<HTMLDivElement>) => {
    const cartoes = [...(grade.current?.querySelectorAll<HTMLElement>('.cartao-modulo') ?? [])];
    const i = cartoes.indexOf(document.activeElement as HTMLElement);
    if (i < 0) return;
    const colunas = Math.max(1, Math.round((grade.current?.clientWidth ?? 1) / (cartoes[0]?.offsetWidth || 1)));
    const alvo = { ArrowRight: i + 1, ArrowLeft: i - 1, ArrowDown: i + colunas, ArrowUp: i - colunas, Home: 0, End: cartoes.length - 1 }[e.key];
    if (alvo === undefined) return;
    e.preventDefault();
    cartoes[Math.max(0, Math.min(cartoes.length - 1, alvo))]?.focus();
  };

  const preCarregar = usePreCarregar();
  const dia = useResumoDia(resumo.data?.modulos.map((m) => m.codigo.toUpperCase()));
  const primeiroNome = sessao?.usuario.nome.split(/\s+/)[0] ?? '';
  const nomeModulo = new Map((resumo.data?.modulos ?? []).map((m) => [m.codigo.toUpperCase(), m.nome]));
  const linkRotina = (i: ItemRotina, props: { className: string; children: ReactNode }) => (
    <Link to="/r/$rotina" params={{ rotina: i.codigo }} className={props.className} {...preCarregar(i.codigo)}>{props.children}</Link>
  );
  const recentes: ItemRotina[] = (resumo.data?.recentes ?? []).slice(0, 8).map((r) => ({ codigo: r.codigo, nome: r.nome, icone: r.icone, detalhe: nomeModulo.get(r.modulo.toUpperCase()) }));
  const favoritos: ItemRotina[] = (resumo.data?.favoritos ?? []).map((f) => ({ codigo: f.codigo, nome: f.nome, icone: f.icone, detalhe: nomeModulo.get(f.modulo.toUpperCase()) }));

  return (
    <div className="inicio">
      <header className="inicio-cabecalho">
        <h1 className="tp-display">{saudacao(new Date().getHours())}, {primeiroNome}</h1>
        <p className="texto-2">
          {dataPorExtenso(dataBase)} · {empresa?.nome}{filial ? ` · Filial ${filial.codigo} ${filial.nome}` : ''}
        </p>
      </header>

      {resumo.data?.avisos.map((a) => (
        <Faixa key={a.tipo} tipo={a.severidade === 'erro' ? 'erro' : 'aviso'} acao={a.rotina ? <Link to="/diagnostico" className="link">Ver diagnóstico</Link> : undefined}>
          {a.mensagem}
        </Faixa>
      ))}
      {resumo.isError && <Faixa tipo="erro" codigo={mensagemDe(resumo.error).codigo}>{mensagemDe(resumo.error).texto}</Faixa>}

      {dia.carregando ? <Esqueleto forma="cartao" linhas={1} /> : <NumerosDia numeros={dia.numeros} />}

      <div className="inicio-corpo">
        <div className="inicio-principal">
          {dia.consultou && (
            <section aria-labelledby="pendencias-titulo" className="inicio-bloco painel">
              <h2 id="pendencias-titulo" className="inicio-secao"><ListChecks size={13} aria-hidden="true" /> Para resolver</h2>
              {dia.carregando ? <Esqueleto linhas={3} /> : <PendenciasDia pendencias={dia.pendencias} nomeModulo={(c) => nomeModulo.get(c)} />}
            </section>
          )}
          <Suspense fallback={null}>
            <Mural />
          </Suspense>
          <section aria-labelledby="modulos-titulo" className="inicio-modulos">
            <h2 id="modulos-titulo" className="inicio-secao">Módulos</h2>
            {resumo.isLoading ? (
              <Esqueleto forma="cartao" linhas={3} />
            ) : (
              <div className="grade-modulos" ref={grade} onKeyDown={moverFoco}>
                {resumo.data?.modulos.map((m) => (
                  <Link key={m.codigo} to="/m/$modulo" params={{ modulo: m.codigo.toLowerCase() }} className="cartao-modulo painel" style={{ viewTransitionName: `modulo-${m.codigo}` }}>
                    <span className="cartao-modulo-icone"><Icone nome={m.icone} size={20} /></span>
                    <span className="cartao-modulo-texto">
                      <span className="cartao-modulo-nome">{m.nome}</span>
                      {m.contador ? <span className="texto-2 cartao-modulo-contador">{m.contador}</span> : <span className="mono texto-3 cartao-modulo-codigo">{m.codigo}</span>}
                    </span>
                    <ArrowUpRight className="cartao-modulo-seta" size={15} aria-hidden="true" />
                  </Link>
                ))}
                {resumo.data?.modulos.length === 0 && <p className="texto-2">Nenhum módulo liberado para o seu usuário nesta empresa. Fale com o administrador.</p>}
              </div>
            )}
          </section>
        </div>

        <aside className="inicio-lateral">
          <section aria-labelledby="favoritos-titulo" className="inicio-bloco painel">
            <h2 id="favoritos-titulo" className="inicio-secao"><Star size={13} aria-hidden="true" /> Favoritos</h2>
            {favoritos.length > 0
              ? <ListaRotinas itens={favoritos} link={linkRotina} />
              : <p className="inicio-dica">Marque uma rotina com <Star size={11} aria-hidden="true" /> ou <kbd className="atalho">Ctrl D</kbd> para ela aparecer aqui.</p>}
          </section>
          <section aria-labelledby="recentes-titulo" className="inicio-bloco painel">
            <h2 id="recentes-titulo" className="inicio-secao"><Clock size={13} aria-hidden="true" /> Recentes</h2>
            {recentes.length > 0
              ? <ListaRotinas itens={recentes} link={linkRotina} />
              : <p className="inicio-dica">As rotinas que você abrir aparecem aqui.</p>}
          </section>
        </aside>
      </div>
    </div>
  );
}
